import React, { useState } from 'react';
import { MoreHorizontal, LogOut } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { NAV_ITEMS, BOTTOM_NAV_IDS, visibleNavItems, flattenNavItems } from '../navItems';

/**
 * The phone navigation bar: four destinations plus More.
 *
 * This app's More sheet is deeper than the other two apps' - eight top-level
 * entries, four of which are on the bar, plus the six children of the
 * Branch Setting group, plus Logout. A phone has no expandable group, so the
 * sheet is built from `flattenNavItems(visibleNavItems(isAdmin))` rather than
 * `NAV_ITEMS` directly: that flattens the Branch Setting children into the
 * same list so every one of them stays reachable, and respects `adminOnly`
 * at both levels so a receptionist never sees an admin-only entry that leads
 * nowhere for them.
 */
const BottomNav = ({ currentPage, onNavigate, onLogout, isAdmin }) => {
  const { print_lang } = useLanguage();
  const [sheetOpen, setSheetOpen] = useState(false);

  const barItems = BOTTOM_NAV_IDS.map((id) => NAV_ITEMS.find((item) => item.id === id));
  // `children` marks a collapsible GROUP, not a page - App.js's renderPage has
  // no case for `branch_setting`, so listing it here opened a blank screen. On a
  // phone the group has no meaning anyway: its children are already listed flat.
  const sheetItems = flattenNavItems(visibleNavItems(isAdmin))
    .filter((item) => !item.children)
    .filter((item) => !BOTTOM_NAV_IDS.includes(item.id));
  const moreIsActive = !BOTTOM_NAV_IDS.includes(currentPage);

  const go = (id) => {
    setSheetOpen(false);
    onNavigate(id);
  };

  return (
    <>
      {sheetOpen && (
        <>
          <div className="more-sheet-scrim" onClick={() => setSheetOpen(false)} />
          <div className="more-sheet" role="dialog" aria-label={print_lang('menu', 'Menu')}>
            <div className="more-sheet-handle" />
            <ul className="more-sheet-list">
              {sheetItems.map(({ id, labelKey, labelFallback, Icon }) => (
                <li key={id}>
                  <button
                    type="button"
                    className={`more-sheet-item ${currentPage === id ? 'active' : ''}`}
                    onClick={() => go(id)}
                  >
                    <Icon size={20} className="more-sheet-icon" aria-hidden="true" />
                    {print_lang(labelKey, labelFallback)}
                  </button>
                </li>
              ))}
              <li>
                <button
                  type="button"
                  className="more-sheet-item more-sheet-item-danger"
                  onClick={() => { setSheetOpen(false); onLogout(); }}
                >
                  <LogOut size={20} className="more-sheet-icon" aria-hidden="true" />
                  {print_lang('logout', 'Logout')}
                </button>
              </li>
            </ul>
          </div>
        </>
      )}

      <nav className="bottom-nav" aria-label={print_lang('menu', 'Menu')}>
        {barItems.map(({ id, labelKey, labelFallback, Icon }) => (
          <button
            key={id}
            type="button"
            className={`bottom-nav-item ${currentPage === id ? 'active' : ''}`}
            onClick={() => go(id)}
          >
            <Icon size={20} className="bottom-nav-icon" aria-hidden="true" />
            <span className="bottom-nav-label">{print_lang(labelKey, labelFallback)}</span>
          </button>
        ))}

        <button
          type="button"
          className={`bottom-nav-item ${moreIsActive ? 'active' : ''}`}
          onClick={() => setSheetOpen((open) => !open)}
          aria-expanded={sheetOpen}
        >
          <MoreHorizontal size={20} className="bottom-nav-icon" aria-hidden="true" />
          <span className="bottom-nav-label">{print_lang('more', 'More')}</span>
        </button>
      </nav>
    </>
  );
};

export default BottomNav;
